/**
 * State Management and Drag Handling Utilities
 *
 * This file contains utility functions for state management and drag handling
 * extracted from MyCanvas.js to improve code organization and maintainability.
 */

/**
 * Find and remove object from state array
 * @param {Array} stateArray - Array to search in
 * @param {Function} setStateFunction - State setter function
 * @param {Function} removedStateSetter - Setter for tracking removed objects
 * @param {string} stateName - Name of the state for debugging
 * @param {Object} selectedObject - Currently selected object
 * @returns {boolean} Whether object was found and removed
 */
export const createFindAndRemoveFromState =
  () =>
  (
    stateArray,
    setStateFunction,
    removedStateSetter,
    stateName,
    selectedObject
  ) => {
    const found = stateArray.find((item) => item.id === selectedObject.id);
    if (found) {
      setStateFunction((prev) => {
        const filtered = prev.filter((item) => item.id !== selectedObject.id);
        // If the object was removed, track it
        if (filtered.length < prev.length) {
          removedStateSetter((prev) => [...prev, selectedObject.id]);
        }
        return filtered;
      });
      return true; // Object was found and removed
    } else {
      // No action needed
    }
    return false; // Object was not found in this state
  };

/**
 * Update text properties
 * @param {string} id - Text element ID
 * @param {Object} newProperties - New properties to apply
 * @param {Function} setTexts - Setter for texts array
 * @param {Function} setOffCanvasTexts - Setter for off-canvas texts array
 * @param {Function} setSelectedText - Setter for selected text
 * @param {Function} saveToHistory - Function to save to history
 */
export const createUpdateTextProperties =
  (setTexts, setOffCanvasTexts, setSelectedText, saveToHistory) =>
  (id, newProperties) => {
    setTexts((prevTexts) =>
      prevTexts.map((text) =>
        text.id === id
          ? {
              ...text,
              ...newProperties,
              fontSize: Number(newProperties.fontSize),
              isFirstTime: false,
            }
          : text
      )
    );

    // Update off-canvas texts as well
    setOffCanvasTexts((prevTexts) =>
      prevTexts.map((text) =>
        text.id === id
          ? {
              ...text,
              ...newProperties,
              fontSize: Number(newProperties.fontSize),
              isFirstTime: false,
            }
          : text
      )
    );

    // Ensure selectedText updates correctly
    setSelectedText((prevSelected) =>
      prevSelected?.id === id
        ? {
            ...prevSelected,
            ...newProperties,
            fontSize: Number(newProperties.fontSize),
            isFirstTime: false,
          }
        : prevSelected
    );
    saveToHistory("update_text_properties");
  };

/**
 * Handle rectangle drag end
 * @param {string} id - Rectangle ID
 * @param {number} x - New X position
 * @param {number} y - New Y position
 * @param {Function} setRectangles - Setter for rectangles array
 * @param {Function} saveToHistory - Function to save to history
 */
export const createHandleRectangleDragEnd =
  (setRectangles, saveToHistory) => (id, x, y) => {
    setRectangles((prevRectangles) => {
      const updated = prevRectangles.map((rect) =>
        rect.id === id ? { ...rect, x, y } : rect
      );
      return updated;
    });

    // Save history immediately
    saveToHistory();
  };

